/**
 * Authentication Context and Provider
 *
 * Manages the complete Microsoft Entra SSO authentication flow:
 * 1. MSAL redirect authentication (not popup - more reliable)
 * 2. Domain validation (@trilogynet.com)
 * 3. API login endpoint handshake
 * 4. ConEx JWT and session storage
 * 5. Session watchdog monitoring
 *
 * Exports useAuth() hook for login/logout functionality.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import type { AuthenticationResult } from '@azure/msal-browser'
import { getMsalInstance, buildLoginRequest } from '@/authConfig'
import { useToast } from '@/contexts/ToastContext'
import { useConfig } from '@/contexts/ConfigContext'
import { setCookieActive, removeCookieActive, getCookieActive } from '@/lib/cookieManager'
import {
  validateDomain,
  extractEmailFromMsalResponse,
  extractTenantIdFromMsalResponse,
} from '@/lib/authValidation'
import { AppLogger } from '@/lib/logger'
import { initializeConex360Storage, Conex360StorageManager } from '@/lib/storageManager'
import { getPendingRedirectResponse } from '@/components/MsalWrapper'
import type { LoginRegistry } from '@generated-schemas'
import type { InferResponse } from '@/types/registryTypes'

type LoginResponse = InferResponse<typeof LoginRegistry>

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Types
// ============================================================================

export interface LogoutOptions {
  /** If true, stores current path so user returns here after signing back in. Defaults to true. */
  preserveRedirect?: boolean
}

export interface LoginOptions {
  /** If true, persist auth state in localStorage across browser restarts. */
  rememberMe?: boolean
}

export interface AuthContextType {
  isUserLoggedIn: boolean
  conExJwt: string | null
  login: (options?: LoginOptions) => Promise<void>
  logout: (options?: LogoutOptions) => Promise<void>
  error: string | null
}

// ============================================================================
// API Response Types
// ============================================================================

type ApiLoginSuccess = LoginResponse['content'] & { success: true }
type ApiLoginError = { success: false; statusCode: number; error: string }
type ApiLoginResponse = ApiLoginSuccess | ApiLoginError

// ============================================================================
// Storage Keys
// ============================================================================

const SESSION_INFO_KEY = 'sessionInfo'
const CONEX_JWT_KEY = 'conExJwt'
const USER_INFO_KEY = 'userInfo'
const REMEMBER_ME_PREFERENCE_KEY = 'conExRememberMe'
const PENDING_REMEMBER_ME_KEY = 'auth-remember-me'

type StorageScope = 'session' | 'local' | 'both'

function getStorageScopeFromRememberMe(rememberMe: boolean): StorageScope {
  return rememberMe ? 'both' : 'session'
}

function writeStorageValue(key: string, value: string, scope: StorageScope): void {
  if (typeof window === 'undefined') return
  if (scope === 'session' || scope === 'both') {
    window.sessionStorage.setItem(key, value)
  }
  if (scope === 'local' || scope === 'both') {
    window.localStorage.setItem(key, value)
  }
}

function removeStorageValue(key: string): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(key)
  window.localStorage.removeItem(key)
}

function readStorageValue(key: string): string | null {
  if (typeof window === 'undefined') return null
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key)
}

function getRememberMePreference(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(REMEMBER_ME_PREFERENCE_KEY) === '1'
}

// ============================================================================
// Module-Level State
// ============================================================================

// Synchronously hydrate JWT from storage at module load time.
// This runs BEFORE any React effects, so fetchFromApi always has a token
// even before AuthProvider's hydration useEffect fires. Without this,
// TanStack Query fires API calls (in child useEffects, which run before
// parent useEffects) with a null JWT, gets 401s, and logout() wipes storage.
const _initialJwt =
  typeof window !== 'undefined'
    ? window.sessionStorage.getItem(CONEX_JWT_KEY) || window.localStorage.getItem(CONEX_JWT_KEY)
    : null

// Store the current auth state at module level
let currentAuthState: AuthContextType = {
  isUserLoggedIn: !!_initialJwt,
  conExJwt: _initialJwt,
  login: async () => {},
  logout: async () => {},
  error: null,
}

// Registry of update callbacks from AuthProvider instances
const authUpdateCallbacks = new Set<(state: AuthContextType) => void>()

/**
 * Update the global auth state and notify all listeners
 */
function updateAuthState(state: AuthContextType): void {
  currentAuthState = state

  // Notify all registered providers
  authUpdateCallbacks.forEach((callback) => {
    try {
      callback(state)
    } catch (error) {
      log.error('Error updating auth state:', error)
    }
  })

  // Dispatch window event for any other listeners
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-context-updated', { detail: state }))
  }
}

/**
 * Get the current auth state
 */
export function getAuthState(): AuthContextType {
  return currentAuthState
}

// ============================================================================
// API Integration Helpers
// ============================================================================

/**
 * Call API login endpoint to exchange MSAL token for ConEx JWT
 *
 * Returns structured response with status information for proper error handling
 */
async function callApiLogin(baseUrl: string, msalAccessToken: string): Promise<ApiLoginResponse> {
  try {
    log.debug('Calling API login', { baseUrl })
    const response = await fetch(`${baseUrl}/api/0.0.1/uniq/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${msalAccessToken}`,
      },
      body: JSON.stringify({}),
    })

    if (response.ok) {
      const data = await response.json()
      const content = data.content ?? data
      log.info('API login profile received:', {
        userNumber: content.profile?.userNumber,
        userStatus: content.profile?.userStatus,
        profileKeys: content.profile ? Object.keys(content.profile) : [],
      })
      if (!content.token || !content.profile) {
        log.error('Missing fields in login response. Keys received:', Object.keys(data))
        return {
          success: false,
          statusCode: response.status,
          error: 'Invalid login response: missing token or profile',
        }
      }
      return {
        success: true,
        token: content.token,
        expiresAt: content.expiresAt,
        profile: content.profile,
      }
    }

    // Error responses
    const errorData = await response.json().catch(() => ({}))
    return {
      success: false,
      statusCode: response.status,
      error: errorData.message || response.statusText,
    }
  } catch (error) {
    // Network error or timeout
    if (error instanceof Error && error.name === 'AbortError') {
      return {
        success: false,
        statusCode: 0,
        error: 'Request timeout',
      }
    }

    log.error('API login fetch error:', error)
    return {
      success: false,
      statusCode: 0,
      error: error instanceof Error ? error.message : 'Network error',
    }
  }
}

/**
 * Call API logout endpoint to revoke ConEx JWT
 */
async function callApiLogout(baseUrl: string): Promise<void> {
  const conExJwt = readStorageValue(CONEX_JWT_KEY)

  if (!conExJwt) {
    return // No token to logout
  }

  try {
    log.debug('Calling API logout', { baseUrl })
    await fetch(`${baseUrl}/api/0.0.1/uniq/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${conExJwt}`,
      },
      body: JSON.stringify({}),
    })
  } catch (error) {
    log.warn('API logout call failed (non-critical):', error)
    // Don't throw - logout should succeed locally regardless
  }
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// ============================================================================
// Provider Component
// ============================================================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Initialize with module-level state
  const [authState, setAuthState] = useState<AuthContextType>(() => getAuthState())
  const { toast } = useToast()
  // apiBaseUrl is resolved by ConfigContext: empty under the Vite dev server
  // (proxy handles /api/*), otherwise the configured API gateway URL.
  const { authConfig, apiBaseUrl } = useConfig()
  const watchdogIntervalRef = useRef<number | null>(null)
  const redirectHandledRef = useRef(false)

  // Hydrate ConEx JWT from sessionStorage on mount (survives page refresh)
  const [conExJwt, setConExJwt] = useState<string | null>(() =>
    readStorageValue(CONEX_JWT_KEY)
  )

  // ========== Complete Login (called after redirect returns) ==========

  const completeLogin = useCallback(
    async (response: AuthenticationResult) => {
      try {
        const msalInstance = getMsalInstance(authConfig ?? undefined)

        log.info('Completing login after redirect', {
          account: response.account?.username,
        })

        // Step 1: Extract email and validate domain (UX-level only)

        const email = extractEmailFromMsalResponse(response.account)
        const tenantId = extractTenantIdFromMsalResponse(response.account)

        if (!validateDomain(email)) {
          log.warn('Domain validation failed', {
            event: 'domain-validation-failed',
            email,
            tid: tenantId,
          })

          toast({
            title: 'Access Restricted',
            description: '@trilogynet.com accounts only',
            type: 'error',
          })

          // Logout from MSAL
          await msalInstance.logoutRedirect()

          throw new Error('Invalid domain')
        }

        // Step 2: Acquire an access token specifically for our backend API.
        // The redirect response's accessToken has audience=Graph (from User.Read).
        // We need a token with audience=api://<clientId> for backend signature verification.
        const apiScopes = authConfig?.authentication?.azure?.scopes?.filter(
          (s: string) => s.startsWith('api://')
        ) ?? []

        let msalAccessToken: string

        if (apiScopes.length > 0 && response.account) {
          log.info('Acquiring API-scoped token via acquireTokenSilent', { apiScopes })
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: apiScopes,
            account: response.account,
          })
          msalAccessToken = tokenResponse.accessToken
        } else {
          // Fallback: use the redirect response token (may fail backend validation)
          log.warn('No API scopes configured, using redirect response token')
          msalAccessToken = response.accessToken
        }


        const loginResponse = await callApiLogin(apiBaseUrl, msalAccessToken)

        if (!loginResponse.success) {
          const statusCode = loginResponse.statusCode

          if (statusCode === 401) {
            toast({
              title: 'Authentication Failed',
              description: 'Your credentials are invalid. Please try signing in again.',
              type: 'error',
            })
            log.error('API login 401 error:', { email })
          } else if (statusCode === 403) {
            toast({
              title: 'Access Denied',
              description: 'Your account is not authorized for this application.',
              type: 'error',
            })
            log.error('API login 403 error:', { email, tid: tenantId })
          } else if (statusCode === 0) {
            toast({
              title: 'Network Error',
              description: 'Request timed out. Please check your connection.',
              type: 'error',
            })
            log.error('API login network error:', loginResponse.error)
          } else {
            toast({
              title: 'Sign In Failed',
              description: 'An error occurred during sign-in. Please try again.',
              type: 'error',
            })
            log.error('API login error:', { statusCode, error: loginResponse.error })
          }

          throw new Error(`API login failed: ${statusCode} ${loginResponse.error}`)
        }

        // Step 3: Store ConEx JWT and session info in storage

        const jwt = loginResponse.token
        const { profile } = loginResponse
        const rememberMe = typeof window !== 'undefined'
          ? window.sessionStorage.getItem(PENDING_REMEMBER_ME_KEY) === '1' || getRememberMePreference()
          : false
        const storageScope = getStorageScopeFromRememberMe(rememberMe)
        if (typeof window !== 'undefined') {
          writeStorageValue(CONEX_JWT_KEY, jwt, storageScope)
          writeStorageValue(SESSION_INFO_KEY, JSON.stringify({ sessionToken: jwt }), storageScope)
          window.localStorage.setItem(REMEMBER_ME_PREFERENCE_KEY, rememberMe ? '1' : '0')
          window.sessionStorage.removeItem(PENDING_REMEMBER_ME_KEY)
        }
        setConExJwt(jwt)

        // Step 4: Store userInfo in sessionStorage for SecurityVerifier

        const firstName = profile.firstName || response.account?.name?.split(' ')[0] || ''
        const lastName = profile.lastName || response.account?.name?.split(' ').slice(1).join(' ') || ''
        const userInfo = {
          userNumber: profile.userNumber,
          userFirstName: firstName,
          userLastName: lastName,
          userEmail: profile.emailAddress,
          userInitials: `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase(),
          userDarkMode: false,
          // Role is no longer derived from the login profile: the backend removed
          // userRoles / jobRoles / permissions from the auth response (legacy role
          // removal; RBAC supplies roles later), and the route-role gate is neutralized,
          // so userRole is intentionally left undefined.
          userRole: undefined as string | undefined,
        }
        // Legacy job-role removal (intermediary stage before RBAC): job_roles /
        // user_job_roles are gone on the backend, so a user no longer needs an assigned
        // role to sign in. Previously login was blocked when no role was present — that
        // dependency is removed here. Restore this block once RBAC / resource roles land.
        // if (!userInfo.userRole) {
        //   toast({
        //     title: 'No Role Assigned',
        //     description: 'Your account has no role assigned. Contact an administrator.',
        //     type: 'error',
        //   })
        //   throw new Error('No role assigned to this account. Contact an administrator.')
        // }

        if (typeof window !== 'undefined') {
          writeStorageValue(USER_INFO_KEY, JSON.stringify(userInfo), storageScope)
        }
        log.debug('Stored userInfo with role', { userRole: userInfo.userRole, userNumber: userInfo.userNumber })

        // Step 5: Initialize conex360 storage with user information

        const userName = `${firstName} ${lastName}`.trim() || email
        initializeConex360Storage(userName, '/')

        // Step 6: Set session cookie (performance optimization)

        // In remember-me mode we align cookie lifetime with JWT expiry.
        const cookieDays = rememberMe && loginResponse.expiresAt
          ? Math.max((loginResponse.expiresAt - Date.now()) / (1000 * 60 * 60 * 24), 1 / 24)
          : 8 / 24 // 8-hour session for standard (non-remember-me) logins
        setCookieActive(cookieDays)

        log.info('User logged in successfully', { email })

        // Navigate to the stored redirect URL or home after successful login
        // The redirect URL is stored in sessionStorage before OAuth flow starts (in signin.tsx)
        const storedRedirect = sessionStorage.getItem('auth-redirect-url')
        sessionStorage.removeItem('auth-redirect-url') // Clean up
        const redirectTo = storedRedirect || '/home'

        // Full page reload to post-login destination.
        // React state is intentionally NOT updated here. Calling setAuthState/updateAuthState
        // with isUserLoggedIn:true would trigger SignInPage's useEffect which calls
        // navigate({to:'/home'}) — a SPA navigation that runs SecurityVerifier's beforeLoad
        // while window.location.href still shows '/signin'. SecurityVerifier then fails the
        // role check for '/home' (fallbackRoute===='/signin') and calls clearSessionStorage(),
        // wiping the JWT before this full reload arrives. Storage is already written above;
        // React auth state is re-hydrated from storage on the new page load.
        window.location.href = redirectTo
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Login failed'

        setConExJwt(null)
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(PENDING_REMEMBER_ME_KEY)
        }
        const errorState: AuthContextType = {
          isUserLoggedIn: false,
          conExJwt: null,
          login: loginRef.current,
          logout: logoutRef.current,
          error: errorMessage,
        }
        setAuthState(errorState)
        updateAuthState(errorState)

        log.error('Login completion error:', { message: errorMessage })
      }
    },
    [authConfig, toast, apiBaseUrl]
  )

  // ========== Check for MSAL redirect response on mount ==========

  useEffect(() => {
    if (redirectHandledRef.current) return

    const response = getPendingRedirectResponse()
    if (response) {
      redirectHandledRef.current = true
      log.info('Found pending MSAL redirect response, completing login')
      completeLogin(response)
    }
  }, [completeLogin])

  // ========== Login Function (triggers redirect) ==========

  const login = useCallback(async (options?: LoginOptions) => {
    try {
      const msalInstance = getMsalInstance(authConfig ?? undefined)
      const redirectUri = authConfig?.authentication?.azure?.redirectUri
      const scopeRequest = buildLoginRequest(authConfig ?? undefined)
      const rememberMe = options?.rememberMe ?? getRememberMePreference()

      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(PENDING_REMEMBER_ME_KEY, rememberMe ? '1' : '0')
        window.localStorage.setItem(REMEMBER_ME_PREFERENCE_KEY, rememberMe ? '1' : '0')
      }

      log.info('Starting login redirect flow', {
        redirectUri,
        scopes: scopeRequest.scopes,
      })

      // Trigger redirect to Microsoft login
      // This will navigate away from the current page
      // When user returns, MsalWrapper's handleRedirectPromise will fire the event
      await msalInstance.loginRedirect({
        ...scopeRequest,
        redirectUri,
      })

      // Note: Code after loginRedirect won't execute because page navigates away
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'

      toast({
        title: 'Sign In Failed',
        description: 'Could not start sign-in process. Please try again.',
        type: 'error',
      })

      log.error('Login redirect error:', { message: errorMessage })
      throw error
    }
  }, [authConfig, toast])

  // ========== Logout Function ==========

  const logout = useCallback(async (options?: LogoutOptions) => {
    // Default to preserving redirect so users return to where they were after re-login
    const preserveRedirect = options?.preserveRedirect ?? true

    try {
      // Step 0: Store current path so user returns here after signing back in
      if (preserveRedirect && typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search
        if (currentPath && currentPath !== '/signin') {
          sessionStorage.setItem('auth-redirect-url', currentPath)
          log.debug('Stored redirect URL for post-login navigation', { currentPath })
        }
      }

      // Step 1: Call API logout endpoint (best effort)
      try {
        await callApiLogout(apiBaseUrl)
      } catch (error) {
        log.warn('API logout error (continuing with local cleanup)', error)
      }

      // Step 2: Clear auth storage in both session/local scopes
      if (typeof window !== 'undefined') {
        removeStorageValue(CONEX_JWT_KEY)
        removeStorageValue(SESSION_INFO_KEY)
        removeStorageValue(USER_INFO_KEY)
        window.sessionStorage.removeItem(PENDING_REMEMBER_ME_KEY)
      }

      // Step 2b: Clear conex360 storage
      Conex360StorageManager.clear()

      // Step 3: Remove session cookie
      removeCookieActive()

      // Step 4: Update auth state
      setConExJwt(null)
      const newState: AuthContextType = {
        isUserLoggedIn: false,
        conExJwt: null,
        login,
        logout,
        error: null,
      }
      setAuthState(newState)
      updateAuthState(newState)

      // Step 5: Clear MSAL local cache without Azure AD server-side logout
      // This keeps the Microsoft account session active (Outlook, Teams, etc.)
      const msalInstance = getMsalInstance(authConfig ?? undefined)
      await msalInstance.clearCache()
      // Manual redirect to sign-in page
      window.location.href = '/signin'

      log.info('User logged out successfully')
    } catch (error) {
      log.error('Logout error:', error)
      // Ensure we still update state even if logout fails
      setConExJwt(null)
      const newState: AuthContextType = {
        isUserLoggedIn: false,
        conExJwt: null,
        login,
        logout,
        error: null,
      }
      setAuthState(newState)
      updateAuthState(newState)
      // Redirect to signin on error
      window.location.href = '/signin'
    }
  }, [authConfig, login, apiBaseUrl])

  // ========== Hydrate module-level auth state on mount ==========
  // On every full page load, currentAuthState resets to the unauthenticated stub
  // (login/logout are empty async () => {}). If the user has a stored JWT they are
  // still authenticated, so we must push the real login/logout functions into
  // currentAuthState immediately. Without this, getAuthState().logout() (called by
  // fetchFromApi on 401) is always a no-op, leaving users trapped in a 401 loop
  // with no automatic redirect to sign-in.

  // Refs always hold the latest login/logout so the [] effect below is never stale
  const loginRef = useRef(login)
  const logoutRef = useRef(logout)
  loginRef.current = login
  logoutRef.current = logout

  useEffect(() => {
    const storedJwt = readStorageValue(CONEX_JWT_KEY)
    if (storedJwt) {
      const hydratedState: AuthContextType = {
        isUserLoggedIn: true,
        conExJwt: storedJwt,
        login: loginRef.current,
        logout: logoutRef.current,
        error: null,
      }
      setAuthState(hydratedState)
      updateAuthState(hydratedState)
      log.debug('Auth state hydrated from stored JWT')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally empty — loginRef/logoutRef always hold current values

  // ========== Watchdog Timer ==========

  useEffect(() => {
    // Skip if user is not logged in
    if (!authState.isUserLoggedIn) {
      if (watchdogIntervalRef.current) {
        clearInterval(watchdogIntervalRef.current)
        watchdogIntervalRef.current = null
      }
      return
    }

    // Set up watchdog interval (60 seconds)
    const interval = setInterval(() => {
      const cookieExists = getCookieActive()

      if (!cookieExists) {
        // Cookie missing = logout immediately, but preserve current path for re-login
        log.warn('Session cookie missing, logging out')
        logout({ preserveRedirect: true })
      }
    }, 60000) // 60 seconds

    watchdogIntervalRef.current = interval as unknown as number

    // Cleanup on unmount
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [authState.isUserLoggedIn, logout])

  // ========== Register Update Callback ==========

  useEffect(() => {
    const handleUpdate = (state: AuthContextType) => {
      setAuthState(state)
    }

    // Register this provider's update callback
    authUpdateCallbacks.add(handleUpdate)

    // Also listen for window events as a fallback
    const handleWindowEvent = (event: Event) => {
      const customEvent = event as CustomEvent<AuthContextType>
      if (customEvent.detail) {
        setAuthState(customEvent.detail)
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('auth-context-updated', handleWindowEvent)
    }

    // Cleanup
    return () => {
      authUpdateCallbacks.delete(handleUpdate)
      if (typeof window !== 'undefined') {
        window.removeEventListener('auth-context-updated', handleWindowEvent)
      }
    }
  }, [])

  const contextValue: AuthContextType = {
    ...authState,
    conExJwt,
    login,
    logout,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
